import React from 'react';
import { InputNumber, Select } from 'antd';
import {
    DownOutlined,
    MinusCircleOutlined,
    PlusOutlined,
    UpOutlined,
} from '@ant-design/icons';
import TrashIcon from '../../../../icons/TrashFilled.svg?react';
import { Button } from '../../../../components/button';
import { LabeledField } from '../../../../components/form-fields/labeled-field';
import { Section } from '../../../../components/form-fields/section';
import { SquareButton } from '../../../../components/action-buttons';
import {
    createMilestone,
    createOption,
    MilestoneForm,
    CouponGroup,
} from './coupon-groups';
import style from './style.module.scss';

// Re-exported so existing consumers of this module (the config page, the
// analytics rewards inventory table) are unaffected by the pure logic having
// moved to its own dependency-free file; see `coupon-groups.ts`'s own header
// comment for why the split exists.
export {
    accumulateTierThresholds,
    buildCouponGroups,
    collapseToOptions,
    createMilestone,
    createOption,
} from './coupon-groups';
export type {
    CouponGroup,
    MilestoneForm,
    MilestoneOptionForm,
} from './coupon-groups';

interface MilestonesSectionProps {
    milestones: MilestoneForm[];
    onChange: (milestones: MilestoneForm[]) => void;
    groups: CouponGroup[];
    /** Per-milestone message, keyed by milestone id. */
    errors: Record<string, string>;
}

/** Matches the drag animation this reorder replaced, so the screen still moves at one speed. */
const REORDER_ANIMATION_MS = 150;

export function MilestonesSection(props: MilestonesSectionProps) {
    const { milestones, onChange, groups, errors } = props;
    const [isExpanded, setIsExpanded] = React.useState(true);
    const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>(
        {}
    );
    const list = React.useRef<HTMLDivElement | null>(null);
    /**
     * Where each card sat just before a reorder, keyed by milestone id.
     *
     * Only populated by `moveMilestone`, so the layout effect below can tell a
     * reorder apart from any other render and leave the rest alone.
     */
    const cardTops = React.useRef<Map<string, number>>(new Map());

    const readCardTops = (): Map<string, number> => {
        const tops = new Map<string, number>();

        for (const card of Array.from(list.current?.children ?? [])) {
            const id = (card as HTMLElement).dataset.milestoneId;
            if (id) {
                tops.set(id, card.getBoundingClientRect().top);
            }
        }

        return tops;
    };

    /**
     * Slides the two swapped cards between their old and new positions, so the
     * eye can follow which coupons moved where.
     *
     * FLIP: the cards are already at their new positions by the time this runs,
     * so each one is offset back to where it was and animated to zero. A layout
     * effect, not a plain effect, because the offset has to be applied before
     * the browser paints or the card visibly lands first and then jumps back.
     */
    React.useLayoutEffect(() => {
        if (cardTops.current.size === 0) {
            return;
        }

        const before = cardTops.current;
        cardTops.current = new Map();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        for (const card of Array.from(list.current?.children ?? [])) {
            const id = (card as HTMLElement).dataset.milestoneId;
            const from = id ? before.get(id) : undefined;
            if (from === undefined) {
                continue;
            }

            const shift = from - card.getBoundingClientRect().top;
            if (shift !== 0) {
                card.animate(
                    [
                        { transform: `translateY(${shift}px)` },
                        { transform: 'translateY(0)' },
                    ],
                    { duration: REORDER_ANIMATION_MS, easing: 'ease-in-out' }
                );
            }
        }
    }, [milestones]);

    const couponOptions = React.useMemo(
        () => groups.map((group) => ({ value: group.key, label: group.label })),
        [groups]
    );

    const updateMilestone = (index: number, next: Partial<MilestoneForm>) => {
        onChange(
            milestones.map((milestone, i) =>
                i === index ? { ...milestone, ...next } : milestone
            )
        );
    };

    /**
     * Moves a milestone's COUPONS to the neighbouring rung; the visit thresholds
     * stay with their position.
     *
     * The alternative, carrying `visits` along with the card, makes every
     * reorder produce a descending ladder that the save guard then rejects, so
     * reordering could never be used without renumbering by hand afterwards.
     */
    const moveMilestone = (index: number, offset: number) => {
        const target = index + offset;
        const next = [...milestones];

        cardTops.current = readCardTops();

        next[index] = {
            ...milestones[target],
            visits: milestones[index].visits,
        };
        next[target] = {
            ...milestones[index],
            visits: milestones[target].visits,
        };

        onChange(next);
    };

    const addMilestone = () => onChange([...milestones, createMilestone()]);

    const removeMilestone = (index: number) =>
        onChange(milestones.filter((_, i) => i !== index));

    const addOption = (index: number) =>
        updateMilestone(index, {
            options: [...milestones[index].options, createOption()],
        });

    const removeOption = (index: number, optionId: string) =>
        updateMilestone(index, {
            options: milestones[index].options.filter(
                (option) => option.id !== optionId
            ),
        });

    const setOptionGroup = (
        index: number,
        optionId: string,
        groupKey: string | null
    ) =>
        updateMilestone(index, {
            options: milestones[index].options.map((option) =>
                option.id === optionId ? { ...option, groupKey } : option
            ),
        });

    const addMilestoneButton = (
        <Button
            variant="secondary"
            icon={<PlusOutlined />}
            onClick={addMilestone}
        >
            Add milestone
        </Button>
    );

    return (
        <Section
            title={`Milestones (${milestones.length})`}
            headerActions={
                <>
                    {addMilestoneButton}
                    <button
                        type="button"
                        className={style.collapse_toggle}
                        aria-label={
                            isExpanded
                                ? 'Collapse milestones'
                                : 'Expand milestones'
                        }
                        onClick={() => setIsExpanded((value) => !value)}
                    >
                        {isExpanded ? <UpOutlined /> : <DownOutlined />}
                    </button>
                </>
            }
            // Collapsed shows the title row only, with no empty card beneath it.
            noCard={!isExpanded}
        >
            {isExpanded ? (
                <div className={style.section_body}>
                    <div className={style.milestone_list} ref={list}>
                        {milestones.map((milestone, index) => {
                            const isCollapsed = collapsed[milestone.id];
                            const error = errors[milestone.id];

                            return (
                                <div
                                    key={milestone.id}
                                    data-milestone-id={milestone.id}
                                    className={style.milestone}
                                >
                                    <span className={style.milestone_index}>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>

                                    <div className={style.milestone_card}>
                                        <div className={style.milestone_header}>
                                            <div
                                                className={
                                                    style.reorder_buttons
                                                }
                                            >
                                                <SquareButton
                                                    icon={<UpOutlined />}
                                                    ariaLabel="Move milestone up"
                                                    disabled={index === 0}
                                                    onClick={() =>
                                                        moveMilestone(index, -1)
                                                    }
                                                />
                                                <SquareButton
                                                    icon={<DownOutlined />}
                                                    ariaLabel="Move milestone down"
                                                    disabled={
                                                        index ===
                                                        milestones.length - 1
                                                    }
                                                    onClick={() =>
                                                        moveMilestone(index, 1)
                                                    }
                                                />
                                            </div>
                                            <div className={style.visits_field}>
                                                <label
                                                    className={
                                                        style.visits_label
                                                    }
                                                    htmlFor={`visits-${milestone.id}`}
                                                >
                                                    Milestone visits
                                                </label>
                                                <InputNumber
                                                    id={`visits-${milestone.id}`}
                                                    className={
                                                        style.visits_input
                                                    }
                                                    min={1}
                                                    controls={false}
                                                    value={milestone.visits}
                                                    onChange={(value) =>
                                                        updateMilestone(index, {
                                                            visits:
                                                                typeof value ===
                                                                'number'
                                                                    ? value
                                                                    : null,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <div
                                                className={
                                                    style.milestone_actions
                                                }
                                            >
                                                <SquareButton
                                                    icon={<TrashIcon />}
                                                    ariaLabel="Remove milestone"
                                                    className={
                                                        style.trash_button
                                                    }
                                                    onClick={() =>
                                                        removeMilestone(index)
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    aria-label={
                                                        isCollapsed
                                                            ? 'Expand milestone'
                                                            : 'Collapse milestone'
                                                    }
                                                    className={
                                                        style.collapse_toggle
                                                    }
                                                    onClick={() =>
                                                        setCollapsed(
                                                            (value) => ({
                                                                ...value,
                                                                [milestone.id]: !isCollapsed,
                                                            })
                                                        )
                                                    }
                                                >
                                                    {isCollapsed ? (
                                                        <DownOutlined />
                                                    ) : (
                                                        <UpOutlined />
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {!isCollapsed && (
                                            <div
                                                className={
                                                    style.milestone_options
                                                }
                                            >
                                                <div
                                                    className={
                                                        style.options_grid
                                                    }
                                                >
                                                    {milestone.options.map(
                                                        (option) => (
                                                            <div
                                                                key={option.id}
                                                                className={
                                                                    style.option_row
                                                                }
                                                            >
                                                                <LabeledField label="Select reward option">
                                                                    <Select
                                                                        showSearch
                                                                        // The column is narrower than a coupon label, so
                                                                        // let the option list size to its own content
                                                                        // instead of inheriting the select's width.
                                                                        dropdownMatchSelectWidth={
                                                                            false
                                                                        }
                                                                        value={
                                                                            option.groupKey ??
                                                                            undefined
                                                                        }
                                                                        placeholder="Select a coupon"
                                                                        options={
                                                                            couponOptions
                                                                        }
                                                                        filterOption={(
                                                                            input,
                                                                            item
                                                                        ) =>
                                                                            String(
                                                                                item?.label ??
                                                                                    ''
                                                                            )
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                    input.toLowerCase()
                                                                                )
                                                                        }
                                                                        onChange={(
                                                                            value
                                                                        ) =>
                                                                            setOptionGroup(
                                                                                index,
                                                                                option.id,
                                                                                typeof value ===
                                                                                    'string'
                                                                                    ? value
                                                                                    : null
                                                                            )
                                                                        }
                                                                    />
                                                                </LabeledField>
                                                                <button
                                                                    type="button"
                                                                    aria-label="Remove option"
                                                                    className={
                                                                        style.remove_option
                                                                    }
                                                                    onClick={() =>
                                                                        removeOption(
                                                                            index,
                                                                            option.id
                                                                        )
                                                                    }
                                                                >
                                                                    <MinusCircleOutlined />
                                                                </button>
                                                            </div>
                                                        )
                                                    )}
                                                </div>

                                                <Button
                                                    variant="secondary"
                                                    icon={<PlusOutlined />}
                                                    onClick={() =>
                                                        addOption(index)
                                                    }
                                                >
                                                    Add option
                                                </Button>
                                            </div>
                                        )}

                                        {error && (
                                            <div
                                                className={
                                                    style.milestone_error
                                                }
                                            >
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className={style.section_footer}>
                        {addMilestoneButton}
                    </div>
                </div>
            ) : null}
        </Section>
    );
}
